import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Field, FieldType, MemberAddeditComponent, MemberAddeditDialogModel } from '../../brokeragency/member-addedit/member-addedit.component';
import { FinanceLedgerService } from '../../../services/finance-ledger/finance-ledger.service';
import { Contract, SettlementBatch } from '../../../services/ledger-client/types';
import { FINANCE_TABS, FinanceTab } from '../finance-tabs';

interface RunSettlementDialogPayload {
  contractId: string;
  periodStart: string | Date;
  periodEnd: string | Date;
  invoiceDueDate?: string | Date;
}

interface InvoiceDialogPayload {
  dueDate: string | Date;
}

@Component({
    selector: 'app-finance-settlements',
    templateUrl: './finance-settlements.component.html',
    styleUrls: ['./finance-settlements.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FinanceSettlementsComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  tabs: FinanceTab[] = FINANCE_TABS;
  activeTab: FinanceTab = this.tabs.find((tab) => tab.id === 'settlements') || this.tabs[0];
  allColumnNames = ['id', 'contractId', 'periodStart', 'periodEnd', 'status', 'totalPayable', 'actionmenu'];
  items = new MatTableDataSource<SettlementBatch>();
  contracts: Contract[] = [];
  selectedSettlementJson = '';

  private readonly destroy$ = new Subject<void>();
  private readonly runSettlementFields: Array<Field> = [
    { name: 'contractId', label: 'Contract Name', fieldType: FieldType.F_DROPDOWN, values: [], required: true },
    { name: 'periodStart', label: 'Period Start', fieldType: FieldType.F_ANYDATE, required: true },
    { name: 'periodEnd', label: 'Period End', fieldType: FieldType.F_ANYDATE, required: true },
    { name: 'invoiceDueDate', label: 'Invoice Due Date (optional)', fieldType: FieldType.F_ANYDATE },
  ];
  private readonly issueInvoiceFields: Array<Field> = [
    { name: 'dueDate', label: 'Invoice Due Date', fieldType: FieldType.F_ANYDATE, required: true },
  ];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private financeLedgerService: FinanceLedgerService,
  ) {}

  ngOnInit(): void {
    this.setActiveTab();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => this.setActiveTab());
    this.reloadContracts();
    this.reloadItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setActiveTab(): void {
    const parsedUrl = this.router.parseUrl(this.router.url);
    const url = parsedUrl.toString().split('?')[0];
    this.activeTab = this.tabs.find((tab) => tab.link === url) || this.tabs[0];
  }

  gotoTab(tab: FinanceTab): void {
    if (this.activeTab?.id === tab.id) {
      return;
    }

    this.router.navigate([tab.link]).then();
    this.activeTab = tab;
  }

  runSettlement(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(null, this.getRunSettlementFields(), 'Settlement Workflow')
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      const payload = this.getValidRunPayload(dialogResult);
      if (!payload) {
        return;
      }

      this.financeLedgerService.runSettlementLifecycle(payload).then((result) => {
        this.selectedSettlementJson = JSON.stringify(result, null, 2);
        this.reloadItems();
        this.snackBar.open('Settlement workflow complete', 'Close', { duration: 3000 });
      }).catch((error: unknown) => {
        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to run settlement workflow'), 'Close', { duration: 3000 });
      });
    });
  }

  postSettlement(item: SettlementBatch): void {
    this.financeLedgerService.postSettlement(item.id).then(() => {
      this.reloadItems();
      this.snackBar.open('Settlement posted', 'Close', { duration: 3000 });
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to post settlement'), 'Close', { duration: 3000 });
    });
  }

  markPaid(item: SettlementBatch): void {
    this.financeLedgerService.markSettlementPaid(item.id).then(() => {
      this.reloadItems();
      this.snackBar.open('Settlement marked paid', 'Close', { duration: 3000 });
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to mark settlement paid'), 'Close', { duration: 3000 });
    });
  }

  issueInvoice(item: SettlementBatch): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(null, this.issueInvoiceFields, 'Issue Invoice')
    });

    dialogRef.afterClosed().subscribe((dialogResult: InvoiceDialogPayload | null) => {
      const dueDate = this.normalizeDate(dialogResult?.dueDate);
      if (!dueDate) {
        return;
      }

      this.financeLedgerService.issueInvoice(item.id, dueDate).then((invoice) => {
        this.snackBar.open(`Invoice ${invoice.invoiceNumber} issued`, 'Close', { duration: 3000 });
      }).catch((error: unknown) => {
        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to issue invoice'), 'Close', { duration: 3000 });
      });
    });
  }

  viewSettlement(item: SettlementBatch): void {
    this.financeLedgerService.getSettlement(item.id).then((settlement) => {
      this.selectedSettlementJson = JSON.stringify(settlement, null, 2);
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load settlement details'), 'Close', { duration: 3000 });
    });
  }

  reloadItems(): void {
    this.financeLedgerService.listSettlements().then((settlements) => {
      this.items.data = settlements.sort((a, b) => b.periodStart.localeCompare(a.periodStart));
      this.items.sort = this.sort;
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load settlements'), 'Close', { duration: 3000 });
    });
  }

  reloadContracts(): void {
    this.financeLedgerService.listContracts().then((contracts) => {
      this.contracts = contracts.sort((contract1, contract2) => contract1.name.localeCompare(contract2.name));
      this.runSettlementFields[0].values = this.contracts.map((contract) => `${contract.name}:${contract.id}`);
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load contracts for settlement'), 'Close', { duration: 3000 });
    });
  }

  private getRunSettlementFields(): Array<Field> {
    return this.runSettlementFields.map((field) => ({ ...field }));
  }

  private getValidRunPayload(payload: RunSettlementDialogPayload | null): {
    contractId: string;
    periodStart: string;
    periodEnd: string;
    invoiceDueDate?: string;
  } | null {
    if (!payload || !payload.contractId || !String(payload.contractId).trim()) {
      this.snackBar.open('contractId is required', 'Close', { duration: 3000 });
      return null;
    }

    const periodStart = this.normalizeDate(payload.periodStart);
    const periodEnd = this.normalizeDate(payload.periodEnd);

    if (!periodStart || !periodEnd) {
      this.snackBar.open('periodStart and periodEnd are required valid dates', 'Close', { duration: 3000 });
      return null;
    }

    const invoiceDueDate = this.normalizeDate(payload.invoiceDueDate);

    return {
      contractId: String(payload.contractId).trim(),
      periodStart,
      periodEnd,
      invoiceDueDate: invoiceDueDate || undefined,
    };
  }

  private normalizeDate(value?: string | Date): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
}
